/**
 * @description Platform branding + LynxPay feature settings page for admin portal
 *
 * Key features:
 * - Identity: platformName, documentTitle, tagline inputs
 * - Colors: primary/secondary color pickers, dark/light mode toggle
 * - Assets: logo upload/delete, favicon upload/delete (via R2)
 * - Meta: themeColor picker, description input
 * - Features: LynxPay (direct) CMS toggle — separate from SO so_features
 * - Company Details: create/edit LynxPay platform company (not Payment Gateways)
 * - Payment Gateways: Elavon enablement + Merchant Alias only
 * - CMS Merchants: collapsible direct-merchant list + Paid/Unpaid hosting drawer (LYN-76)
 * - Live preview panel showing current color/logo/name combination
 *
 * @dependencies
 * - adminApi: getPlatformConfig, updatePlatformBranding, updatePlatformFeatures,
 *             uploadPlatformLogo, uploadPlatformFavicon,
 *             deletePlatformLogo, deletePlatformFavicon,
 *             getDirectCmsMerchants
 * - MerchantDrawer: websites Paid hosting toggle
 * - lucide-react: icons
 *
 * @notes
 * - Mirrors BrandingTab in TenantDetail.js but as a standalone page
 * - Platform config is a singleton (no ID needed in API calls)
 * - Assets are managed via dedicated upload/delete endpoints (not PUT /branding)
 * - PUT /branding preserves asset URLs via server-side merge
 * - CMS toggle here gates direct LynxPay merchants only; SO tenants use Tenants → Configurations
 * - Merchants Status (Active/Inactive/Unlinked) ≠ Hosting (Free/Paid) — never conflate
 */

import { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle, Save, Loader2, Check,
  Upload, Trash2, Image, Sun, Moon, Palette, ToggleLeft,
  ChevronDown, ChevronRight, ChevronLeft, Users
} from 'lucide-react';
import {
  getPlatformConfig,
  updatePlatformBranding,
  updatePlatformFeatures,
  uploadPlatformLogo,
  uploadPlatformFavicon,
  deletePlatformLogo,
  deletePlatformFavicon,
  getDirectCmsMerchants,
  getPlatformGateways,
  getPlatformActorCompany,
  createPlatformActorCompany,
  updatePlatformActorCompany
} from '../../services/api/adminApi';
import MerchantDrawer from '../../components/merchants/MerchantDrawer';
import MerchantAliasEditor from '../../components/merchants/MerchantAliasEditor';
import {
  merchantAccountStatusBadge,
  resolveMerchantAccountStatus,
} from '../../utils/merchantAccountStatus';
import { resolveMerchantCompanyId } from '../../utils/resolveMerchantCompanyId';

const MERCHANTS_PAGE_SIZE = 5;

// ── Defaults ────────────────────────────────────────────────

const DEFAULT_IDENTITY = {
  platformName: '',
  documentTitle: '',
  tagline: '',
};

const DEFAULT_COLORS = {
  mode: 'dark',
  primaryColor: '#facc15',
  secondaryColor: '#eab308',
};

const DEFAULT_META = {
  themeColor: '#000000',
  description: '',
};

// ── Reusable Components ─────────────────────────────────────

function SectionCard({ title, children, className = '' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="px-4 py-3 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-900">{title}</p>
      </div>
      <div className={`px-4 py-3 ${className}`}>{children}</div>
    </div>
  );
}

function ColorInput({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={onChange}
          className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={onChange}
          className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
        />
      </div>
    </div>
  );
}

function TextInput({ label, value, onChange, placeholder, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
      />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function AssetUpload({ label, url, uploading, accept, hint, onUpload, onDelete }) {
  if (url) {
    const isLogo = label === 'Logo';
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="flex items-center gap-4">
          <div className={`${isLogo ? 'w-32 h-16 p-2' : 'w-12 h-12 p-1.5'} border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden`}>
            <img src={url} alt={label} className="max-w-full max-h-full object-contain" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-500 font-medium cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              Replace
              <input type="file" accept={accept} onChange={onUpload} className="hidden" />
            </label>
            <button
              onClick={onDelete}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-500 font-medium disabled:opacity-50"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Remove
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <label className={`flex items-center gap-3 p-4 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-colors ${label === 'Logo' ? 'max-w-sm' : 'max-w-xs'}`}>
        {uploading ? (
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        ) : (
          <Image className="w-6 h-6 text-gray-400" />
        )}
        <div>
          <p className="text-sm font-medium text-gray-700">Upload {label.toLowerCase()}</p>
          <p className="text-xs text-gray-400">{hint}</p>
        </div>
        <input type="file" accept={accept} onChange={onUpload} className="hidden" />
      </label>
    </div>
  );
}

// ── CMS Merchants (LynxPay-direct) ────────────────────────

/**
 * Collapsible Merchants list under Settings → CMS.
 * Status (Active/Inactive) and Hosting (Free/Paid) are separate columns.
 */
function CmsMerchantsPanel() {
  const [expanded, setExpanded] = useState(false);
  const [merchants, setMerchants] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: MERCHANTS_PAGE_SIZE,
    offset: 0,
    hasMore: false,
  });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchMerchants = useCallback(async (pageIndex) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getDirectCmsMerchants({
        limit: MERCHANTS_PAGE_SIZE,
        offset: pageIndex * MERCHANTS_PAGE_SIZE,
      });
      if (result.success) {
        setMerchants(result.data || []);
        setPagination(
          result.pagination || {
            total: 0,
            limit: MERCHANTS_PAGE_SIZE,
            offset: pageIndex * MERCHANTS_PAGE_SIZE,
            hasMore: false,
          }
        );
      } else {
        setError(result.error?.message || 'Failed to load merchants');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load merchants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded) {
      fetchMerchants(page);
    }
  }, [expanded, page, fetchMerchants]);

  const handleMerchantUpdated = (updated) => {
    setMerchants((prev) =>
      prev.map((m) =>
        resolveMerchantCompanyId(m) === updated.companyId
          ? {
              ...m,
              cmsSiteId: updated.cmsSiteId ?? m.cmsSiteId,
              hostingTier: updated.hostingTier,
              slug: updated.slug ?? m.slug,
              status: updated.status ?? m.status,
              defaultHostname: updated.defaultHostname ?? m.defaultHostname,
              defaultBaseDomain: updated.defaultBaseDomain ?? m.defaultBaseDomain,
            }
          : m
      )
    );
    setSelected((prev) =>
      prev && resolveMerchantCompanyId(prev) === updated.companyId
        ? {
            ...prev,
            cmsSiteId: updated.cmsSiteId ?? prev.cmsSiteId,
            hostingTier: updated.hostingTier,
            slug: updated.slug ?? prev.slug,
            status: updated.status ?? prev.status,
            defaultHostname: updated.defaultHostname ?? prev.defaultHostname,
            defaultBaseDomain: updated.defaultBaseDomain ?? prev.defaultBaseDomain,
          }
        : prev
    );
  };

  const hostingLabel = (tier) => {
    if (!tier) return '—';
    return tier === 'paid' ? 'Paid' : 'Free';
  };

  return (
    <div className="border-t border-gray-100 pt-4 mt-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">Merchants</p>
            <p className="text-xs text-gray-500">
              LynxPay-direct merchants — flip websites Paid/Unpaid hosting
            </p>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="mt-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          )}

          {!loading && error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && merchants.length === 0 && (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500">No direct LynxPay merchants found</p>
            </div>
          )}

          {!loading && !error && merchants.length > 0 && (
            <>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/60">
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Business
                      </th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hosting
                      </th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {merchants.map((merchant) => (
                      <tr
                        key={resolveMerchantCompanyId(merchant)}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() =>
                          setSelected({
                            ...merchant,
                            companyId: resolveMerchantCompanyId(merchant),
                          })
                        }
                      >
                        <td className="px-3 py-2.5">
                          <p className="text-sm font-medium text-gray-900">
                            {merchant.businessName}
                          </p>
                        </td>
                        <td className="px-3 py-2.5">
                          {(() => {
                            const status = resolveMerchantAccountStatus(merchant);
                            const badge = merchantAccountStatusBadge(status);
                            return (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                              >
                                {badge.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              merchant.hostingTier === 'paid'
                                ? 'bg-indigo-50 text-indigo-700'
                                : merchant.hostingTier
                                  ? 'bg-gray-100 text-gray-600'
                                  : 'bg-gray-50 text-gray-400'
                            }`}
                          >
                            {hostingLabel(merchant.hostingTier)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-gray-400">
                          {merchant.createdAt
                            ? new Date(merchant.createdAt).toLocaleDateString()
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {pagination.total} merchant{pagination.total !== 1 ? 's' : ''}
                  {pagination.total > 0 && (
                    <>
                      {' '}
                      · showing {pagination.offset + 1}–
                      {Math.min(pagination.offset + merchants.length, pagination.total)}
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={page === 0 || loading}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={!pagination.hasMore || loading}
                    onClick={() => setPage((p) => p + 1)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    Next
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <MerchantDrawer
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        merchant={selected}
        onMerchantUpdated={handleMerchantUpdated}
      />
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────

/**
 * Settings / Platform company + Payment Gateways (LYN-223).
 * Company Details is LynxPay-the-platform. Payment Gateways is Elavon + alias only.
 * Integrator credentials stay on Integrations → Gateways.
 */
const PLATFORM_COMPANY_DEFAULTS = {
  businessName: 'LynxPay',
  businessType: 'c_corporation',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  accountingMethod: 'accrual',
  ein: ''
};

function actorToForm(actor) {
  if (!actor) return { ...PLATFORM_COMPANY_DEFAULTS };
  return {
    businessName: actor.businessName || 'LynxPay',
    businessType: actor.businessType || 'c_corporation',
    addressLine1: actor.addressLine1 || '',
    addressLine2: actor.addressLine2 || '',
    city: actor.city || '',
    state: actor.state || '',
    postalCode: actor.postalCode || '',
    accountingMethod: actor.accountingMethod || 'accrual',
    ein: actor.ein || ''
  };
}

function PlatformCompanyFields({ form, setForm }) {
  const fieldClass = 'mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm';
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <label className="text-xs text-gray-700 sm:col-span-2">
        Business name
        <input
          required
          value={form.businessName}
          onChange={(e) => setForm((prev) => ({ ...prev, businessName: e.target.value }))}
          className={fieldClass}
        />
      </label>
      <label className="text-xs text-gray-700">
        Entity type
        <select
          required
          value={form.businessType}
          onChange={(e) => setForm((prev) => ({ ...prev, businessType: e.target.value }))}
          className={fieldClass}
        >
          <option value="c_corporation">C Corporation</option>
          <option value="s_corporation">S Corporation</option>
          <option value="llc">LLC</option>
          <option value="sole_proprietorship">Sole Proprietorship</option>
          <option value="partnership">Partnership</option>
          <option value="nonprofit">Nonprofit (Form 990)</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label className="text-xs text-gray-700">
        Accounting method
        <select
          required
          value={form.accountingMethod}
          onChange={(e) => setForm((prev) => ({ ...prev, accountingMethod: e.target.value }))}
          className={fieldClass}
        >
          <option value="accrual">Accrual</option>
          <option value="cash">Cash</option>
        </select>
      </label>
      <label className="text-xs text-gray-700 sm:col-span-2">
        Address
        <input
          required
          value={form.addressLine1}
          onChange={(e) => setForm((prev) => ({ ...prev, addressLine1: e.target.value }))}
          className={fieldClass}
        />
      </label>
      <label className="text-xs text-gray-700 sm:col-span-2">
        Address line 2
        <input
          value={form.addressLine2}
          onChange={(e) => setForm((prev) => ({ ...prev, addressLine2: e.target.value }))}
          className={fieldClass}
        />
      </label>
      <label className="text-xs text-gray-700">
        City
        <input
          required
          value={form.city}
          onChange={(e) => setForm((prev) => ({ ...prev, city: e.target.value }))}
          className={fieldClass}
        />
      </label>
      <label className="text-xs text-gray-700">
        State
        <input
          required
          maxLength={2}
          value={form.state}
          onChange={(e) => setForm((prev) => ({ ...prev, state: e.target.value.toUpperCase() }))}
          className={fieldClass}
        />
      </label>
      <label className="text-xs text-gray-700">
        ZIP
        <input
          required
          value={form.postalCode}
          onChange={(e) => setForm((prev) => ({ ...prev, postalCode: e.target.value }))}
          className={fieldClass}
        />
      </label>
      <label className="text-xs text-gray-700">
        EIN (optional)
        <input
          value={form.ein}
          onChange={(e) => setForm((prev) => ({ ...prev, ein: e.target.value }))}
          placeholder="XX-XXXXXXX"
          className={fieldClass}
        />
      </label>
    </div>
  );
}

function PlatformCompanyAndGateways() {
  const [gateways, setGateways] = useState([]);
  const [actor, setActor] = useState(null);
  const [actorError, setActorError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(PLATFORM_COMPANY_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [gwResult, actorResult] = await Promise.allSettled([
          getPlatformGateways(),
          getPlatformActorCompany()
        ]);
        if (cancelled) return;
        if (gwResult.status === 'fulfilled') {
          setGateways(gwResult.value.data || []);
        }
        const actorBody = actorResult.status === 'fulfilled'
          ? actorResult.value
          : actorResult.reason?.response?.data;
        const actorStatus = actorResult.status === 'rejected'
          ? actorResult.reason?.response?.status
          : null;
        if (actorBody?.success && actorBody.data) {
          setActor(actorBody.data);
          setForm(actorToForm(actorBody.data));
          setActorError(null);
        } else if (actorStatus === 404 || actorBody?.error?.code === 'PLATFORM_COMPANY_MISSING') {
          setActor(null);
          setActorError(null);
        } else {
          const err = actorBody?.error;
          setActorError(err?.message || 'No platform company found for this Super Admin.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const elavonEnabled = gateways.some((gw) => gw.gatewayType === 'elavon' && gw.isEnabled);

  const handleSubmitCompanyDetails = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    setSaved(false);
    try {
      const payload = {
        ...form,
        ein: form.ein || null
      };
      const result = actor
        ? await updatePlatformActorCompany(payload)
        : await createPlatformActorCompany(payload);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to save platform company');
      }
      setActor(result.data);
      setForm(actorToForm(result.data));
      setActorError(null);
      setSaved(true);
    } catch (err) {
      const existing = err.response?.data?.data;
      if (err.response?.status === 409 && existing?.id && !actor) {
        setActor(existing);
        setForm(actorToForm(existing));
        setActorError(null);
      } else {
        setFormError(
          err.response?.data?.error?.message ||
            err.message ||
            'Failed to save platform company'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SectionCard title="Company Details" className="space-y-4">
        <p className="text-xs text-gray-500">
          LynxPay-the-platform company. This is not a merchant drawer row.
          Merchant Alias stays in Payment Gateways.
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading company...
          </div>
        ) : (
          <form onSubmit={handleSubmitCompanyDetails} className="space-y-3">
            {!actor && (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Create this company before you can save a Merchant Alias.
                {actorError ? ` ${actorError}` : ''}
              </p>
            )}
            <PlatformCompanyFields form={form} setForm={(next) => {
              setForm(next);
              setSaved(false);
            }} />
            {formError && <p className="text-xs text-red-700">{formError}</p>}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-500 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {actor ? (saved ? 'Saved' : 'Save company details') : 'Create platform company'}
            </button>
          </form>
        )}
      </SectionCard>

      <SectionCard title="Payment Gateways" className="space-y-4">
        <p className="text-xs text-gray-500">
          Shared integrator credentials stay on Integrations → Gateways. Merchant Alias
          is stored on the LynxPay platform company when Elavon is enabled.
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading gateways...
          </div>
        ) : (
          <div className="space-y-2">
            {gateways.length === 0 ? (
              <p className="text-xs text-gray-500">
                No platform gateways configured. Add Elavon under Integrations → Gateways first.
              </p>
            ) : (
              gateways.map((gw) => (
                <div
                  key={gw.id}
                  className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{gw.gatewayName}</p>
                    <p className="text-xs text-gray-500">{gw.gatewayType}</p>
                  </div>
                  <span className={`text-xs font-medium ${gw.isEnabled ? 'text-green-700' : 'text-gray-500'}`}>
                    {gw.isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
        {elavonEnabled && actor && (
          <MerchantAliasEditor
            companyId={actor.id}
            elavonAssigned
            initialAlias={actor.merchantAlias || ''}
            onSaved={(data) => setActor((prev) => ({ ...prev, ...data }))}
          />
        )}
        {elavonEnabled && !actor && (
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Create the platform company under Company Details to save a Merchant Alias.
          </p>
        )}
      </SectionCard>
    </>
  );
}

export default function PlatformSettings() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branding, setBranding] = useState(null);

  const [identity, setIdentity] = useState(DEFAULT_IDENTITY);
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [meta, setMeta] = useState(DEFAULT_META);

  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [cmsEnabled, setCmsEnabled] = useState(true);
  const [savedCmsEnabled, setSavedCmsEnabled] = useState(true);
  const [savingFeatures, setSavingFeatures] = useState(false);
  const [featuresSaveResult, setFeaturesSaveResult] = useState(null);

  const fetchConfig = useCallback(async ({ preserveCmsDraft = false } = {}) => {
    try {
      const response = await getPlatformConfig();
      if (response.success && response.data) {
        const b = response.data.branding || {};
        setBranding(b);
        setIdentity(b.identity ? { ...DEFAULT_IDENTITY, ...b.identity } : DEFAULT_IDENTITY);
        setColors(b.colors ? { ...DEFAULT_COLORS, ...b.colors } : DEFAULT_COLORS);
        setMeta(b.meta ? { ...DEFAULT_META, ...b.meta } : DEFAULT_META);
        const enabled = response.data.features?.cms?.isEnabled !== false;
        setSavedCmsEnabled(enabled);
        // Branding/asset saves refetch config; keep an unsaved CMS toggle intact.
        if (!preserveCmsDraft) {
          setCmsEnabled(enabled);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load platform config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Shared asset action handler — eliminates upload/delete duplication
  const handleAssetAction = useCallback(async (actionFn, type, cleanup = () => {}) => {
    setUploading(type);
    setUploadError(null);
    try {
      await actionFn();
      await fetchConfig({ preserveCmsDraft: true });
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || `Failed to process ${type}`);
    } finally {
      setUploading(null);
      cleanup();
    }
  }, [fetchConfig]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleAssetAction(() => uploadPlatformLogo(file), 'logo', () => (e.target.value = ''));
  };

  const handleFaviconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleAssetAction(() => uploadPlatformFavicon(file), 'favicon', () => (e.target.value = ''));
  };

  const handleDeleteLogo = () => handleAssetAction(deletePlatformLogo, 'logo');
  const handleDeleteFavicon = () => handleAssetAction(deletePlatformFavicon, 'favicon');

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      await updatePlatformBranding({ identity, colors, meta });
      setSaveResult('success');
      await fetchConfig({ preserveCmsDraft: true });
      setTimeout(() => setSaveResult(null), 2000);
    } catch (err) {
      setSaveResult(err.response?.data?.error?.message || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeatures = async () => {
    setSavingFeatures(true);
    setFeaturesSaveResult(null);
    try {
      await updatePlatformFeatures({ cms: { isEnabled: cmsEnabled } });
      setSavedCmsEnabled(cmsEnabled);
      setFeaturesSaveResult('success');
      setTimeout(() => setFeaturesSaveResult(null), 2000);
    } catch (err) {
      setFeaturesSaveResult(err.response?.data?.error?.message || 'Failed to update features');
    } finally {
      setSavingFeatures(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const logoUrl = branding?.assets?.logoUrl || null;
  const faviconUrl = branding?.assets?.faviconUrl || null;
  const displayName = identity.platformName.trim() || 'LynxPay';

  return (
    <div className="max-w-3xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Palette className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Platform Branding</h1>
            <p className="text-sm text-gray-500">
              Default branding for app.lynxpay.us. SO tenants can override with their own branding.
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : saveResult === 'success' ? (
            <Check className="w-3.5 h-3.5" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          {saving ? 'Saving...' : saveResult === 'success' ? 'Saved' : 'Save Changes'}
        </button>
      </div>

      {/* Save Error */}
      {typeof saveResult === 'string' && saveResult !== 'success' && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{saveResult}</span>
        </div>
      )}

      {/* Upload Error */}
      {uploadError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{uploadError}</span>
        </div>
      )}

      <div className="space-y-6">
        <PlatformCompanyAndGateways />

        {/* LynxPay features — direct merchants only; SO CMS stays on Tenants → Configurations */}
        <SectionCard title="LynxPay Features" className="space-y-4">
          <p className="text-xs text-gray-500">
            Controls features for direct LynxPay merchants (app.lynxpay.us). Sales Organization
            CMS flags are managed per tenant under Tenants → Configurations — this toggle does
            not change SO entitlements.
          </p>
          <div className="flex items-center justify-between gap-4 py-1">
            <div className="flex items-start gap-2.5 min-w-0">
              <ToggleLeft className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">CMS</p>
                <p className="text-xs text-gray-500">
                  Website tab and site provisioning for direct LynxPay merchants
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={cmsEnabled}
              aria-label="LynxPay CMS"
              onClick={() => setCmsEnabled((prev) => !prev)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                cmsEnabled ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                  cmsEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {typeof featuresSaveResult === 'string' && featuresSaveResult !== 'success' && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{featuresSaveResult}</span>
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSaveFeatures}
              disabled={savingFeatures || cmsEnabled === savedCmsEnabled}
              className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-50 transition-colors"
            >
              {savingFeatures ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : featuresSaveResult === 'success' ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              {savingFeatures
                ? 'Saving...'
                : featuresSaveResult === 'success'
                  ? 'Saved'
                  : 'Save Features'}
            </button>
          </div>

          <CmsMerchantsPanel />
        </SectionCard>

        {/* Identity */}
        <SectionCard title="Identity" className="space-y-4">
          <TextInput
            label="Platform Name"
            value={identity.platformName}
            onChange={(e) => setIdentity(p => ({ ...p, platformName: e.target.value }))}
            placeholder="LynxPay"
            hint="Displayed in the portal header and sidebar"
          />
          <TextInput
            label="Document Title"
            value={identity.documentTitle}
            onChange={(e) => setIdentity(p => ({ ...p, documentTitle: e.target.value }))}
            placeholder={identity.platformName || 'LynxPay'}
            hint="Brand name shown in browser tabs (e.g. 'Dashboard | YourBrand'). Defaults to platform name."
          />
          <TextInput
            label="Tagline"
            value={identity.tagline}
            onChange={(e) => setIdentity(p => ({ ...p, tagline: e.target.value }))}
            placeholder="e.g. Automated Payment Solutions"
            hint="Shown in the footer and auth pages"
          />
        </SectionCard>

        {/* Colors */}
        <SectionCard title="Colors" className="space-y-4">
          <div className="grid grid-cols-2 gap-4 max-w-sm">
            <ColorInput
              label="Primary Color"
              value={colors.primaryColor}
              onChange={(e) => setColors(p => ({ ...p, primaryColor: e.target.value }))}
            />
            <ColorInput
              label="Secondary Color"
              value={colors.secondaryColor}
              onChange={(e) => setColors(p => ({ ...p, secondaryColor: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Mode</label>
            <div className="flex items-center gap-2 max-w-xs">
              <button
                onClick={() => setColors(p => ({ ...p, mode: 'dark' }))}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  colors.mode === 'dark'
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Moon className="w-3.5 h-3.5" />
                Dark
              </button>
              <button
                onClick={() => setColors(p => ({ ...p, mode: 'light' }))}
                className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  colors.mode === 'light'
                    ? 'bg-white text-gray-900 border-gray-900 ring-1 ring-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Sun className="w-3.5 h-3.5" />
                Light
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Default theme for merchants. Users can override in settings.</p>
          </div>
        </SectionCard>

        {/* Assets */}
        <SectionCard title="Assets" className="space-y-5">
          <AssetUpload
            label="Logo"
            url={logoUrl}
            uploading={uploading === 'logo'}
            accept="image/svg+xml,image/png,image/jpeg"
            hint="Wordmark in the app and emails, and the image when a LynxPay link is shared. PNG or JPEG recommended for share cards."
            onUpload={handleLogoUpload}
            onDelete={handleDeleteLogo}
          />
          <AssetUpload
            label="Favicon"
            url={faviconUrl}
            uploading={uploading === 'favicon'}
            accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml"
            hint="Browser tab icon. PNG is also used as the LynxPay home-screen icon. ICO/SVG stay tab-only."
            onUpload={handleFaviconUpload}
            onDelete={handleDeleteFavicon}
          />
        </SectionCard>

        {/* Meta */}
        <SectionCard title="Meta" className="space-y-4">
          <ColorInput
            label="Theme Color"
            value={meta.themeColor}
            onChange={(e) => setMeta(p => ({ ...p, themeColor: e.target.value }))}
          />
          <TextInput
            label="Description"
            value={meta.description}
            onChange={(e) => setMeta(p => ({ ...p, description: e.target.value }))}
            placeholder="e.g. Unified merchant management portal"
            hint="SEO description for search engines"
          />
        </SectionCard>

        {/* Live Preview */}
        <SectionCard title="Preview" className="py-4">
          {/* Header mockup */}
          <div
            className="rounded-t-lg px-4 py-3 flex items-center justify-between"
            style={{ backgroundColor: colors.mode === 'dark' ? '#0f172a' : '#f8fafc' }}
          >
            <div className="flex items-center gap-2.5">
              {logoUrl ? (
                <img src={logoUrl} alt="" className="h-7 w-auto" />
              ) : (
                <div
                  className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: colors.primaryColor }}
                >
                  {displayName.charAt(0)}
                </div>
              )}
              <span
                className="text-sm font-semibold"
                style={{ color: colors.mode === 'dark' ? '#f8fafc' : '#0f172a' }}
              >
                {displayName}
              </span>
            </div>
            <div
              className="w-6 h-6 rounded-full"
              style={{ backgroundColor: colors.mode === 'dark' ? '#1e293b' : '#e2e8f0' }}
            />
          </div>

          {/* Body mockup */}
          <div
            className="px-4 py-4 rounded-b-lg border-t"
            style={{
              backgroundColor: colors.mode === 'dark' ? '#1e293b' : '#ffffff',
              borderColor: colors.mode === 'dark' ? '#334155' : '#e2e8f0',
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <button
                className="px-3 py-1.5 rounded-md text-xs font-medium text-white"
                style={{ backgroundColor: colors.primaryColor }}
              >
                Primary Button
              </button>
              <button
                className="px-3 py-1.5 rounded-md text-xs font-medium text-white"
                style={{ backgroundColor: colors.secondaryColor }}
              >
                Secondary
              </button>
            </div>
            <p className="text-xs" style={{ color: colors.mode === 'dark' ? '#94a3b8' : '#64748b' }}>
              {identity.tagline || 'Tagline appears here'}
            </p>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
